import React, { FunctionComponent, useEffect, useState } from 'react'
import { Car } from 'models/Car'
import {
  Chip,
  FormHelperText,
  Input,
  InputAdornment,
  InputLabel, useMediaQuery,
  withStyles,
} from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import DriveEtaIcon from '@material-ui/icons/DriveEta'
import AddIcon from '@material-ui/icons/Add'
import { Alert, Autocomplete } from '@material-ui/lab'
import { useSubscription } from 'components/providers/SubscriptionsProvider'
import { useTranslation } from 'react-i18next'
import Button from '@material-ui/core/Button'
import { useTheme } from '@material-ui/core/styles'

const VehicleInput = withStyles(theme => ({
  input: {
    backgroundColor: '#fff',
    textTransform: 'uppercase',
    fontFamily: '"Roboto Mono", monospace',
    lineHeight: '18px',
  },
}))(Input)

export type Props = {
  selectedCars: Car[]
  extraCars?: Car[]
}
export type Functions = {
  onChange(checked: Car[])
  onAddCar(car: Car)
}
export type AllProps = Props & Functions
export const VehicleManager: FunctionComponent<AllProps> = ({
  onChange,
  selectedCars,
  extraCars,
  onAddCar,
}) => {
  const [checked, setChecked] = useState<Car[]>(selectedCars)
  const [newPlateNumbers, setNewPlateNumbers] = useState('')
  const [error, setError] = useState(null)
  const { cars } = useSubscription()
  const { t } = useTranslation()
  const theme = useTheme();
  const smallDevice = useMediaQuery(theme.breakpoints.down('xs'));

  useEffect(() => {
    onChange(checked)
  }, [checked])

  const hasError = Boolean(error)
  return (
    <div>
      <div style={{ margin: '0 -8px' }}>
        {checked.map((car, key) => (
          <Chip
            label={car.plateNumber}
            variant="outlined"
            color="secondary"
            key={key}
            onDelete={() => {
              setChecked(
                checked.reduce((acc, current) => {
                  if (current.plateNumber !== car.plateNumber) {
                    acc = [...acc, current]
                  }

                  return acc
                }, []),
              )
            }}
            style={{ margin: 8, fontFamily: '"Roboto Mono", monospace' }}
          />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          marginTop: '40px',
          flexDirection: 'column',
        }}
      >
        {Boolean(error) && (
          <Alert
            severity="error"
            style={{
              marginBottom: '20px',
              maxWidth: '360px',
              width: '100%',
            }}
          >
            {error}
          </Alert>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            inputValue={newPlateNumbers}
            onInputChange={(event, value) => {
              const sanitizedValue = value.toUpperCase().replace(/[^A-Z0-9, ]/g, '')

              if (value.toUpperCase() == sanitizedValue) {
                setNewPlateNumbers(value)

                return
              }

              const target = event.target as HTMLInputElement
              const valueLengthDiff = sanitizedValue.length - value.length
              const selectionStart = target.selectionStart + valueLengthDiff
              const selectionEnd = target.selectionEnd + valueLengthDiff

              setNewPlateNumbers(sanitizedValue)

              setTimeout(target => {
                target.selectionStart = selectionStart
                target.selectionEnd = selectionEnd
              }, 0, target)
            }}
            options={cars.filter(car => !checked.find(checkedCar => checkedCar.plateNumber == car.plateNumber)).map(car => car.plateNumber)}
            freeSolo
            renderInput={(params) => (
              <FormControl error={hasError} ref={params.InputProps.ref}>
                <InputLabel>
                  {t('plateNumbers')}
                </InputLabel>
                <VehicleInput
                  {...params.inputProps}
                  startAdornment={
                    <InputAdornment position="start">
                      <DriveEtaIcon />
                    </InputAdornment>
                  }
                />
                <FormHelperText>{`${t('example')}: AB1234, CD5678`}</FormHelperText>
              </FormControl>
            )}
            style={{maxWidth: 200, marginRight: 12 }}
          />
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={!smallDevice && <AddIcon />}
            onClick={() => {
              const normalizedNewPlateNumbers = newPlateNumbers.toUpperCase()

              if (!/[A-Z0-9]/.test(normalizedNewPlateNumbers)) {
                return setError(
                  t('subscriptionPurchase.alertProvideVehiclePlateNumber'),
                )
              }

              const newPlateNumbersList = Array.from(new Set(normalizedNewPlateNumbers
                .split(/[, ]+/)
                .map(plateNumber => plateNumber.replace(/ /g, ''))
                .filter(plateNumber => plateNumber.length)))

              const newChecked = [...checked]

              for (let plateNumber of newPlateNumbersList) {
                if (checked.find(car => car.plateNumber == plateNumber)) {
                  return setError(
                    t('subscriptionPurchase.alertPlateNumberAlreadyAdded', { plate: plateNumber }),
                  )
                }

                newChecked.push(new Car({ plateNumber }))
              }

              setError(null)
              setChecked(newChecked)
              setNewPlateNumbers('')
            }}
          >
            {t('add')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default VehicleManager
